import React from 'react';
import { Box, Typography, Card, Button, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import InboxIcon from '@mui/icons-material/Inbox';
import { alpha } from '@mui/material/styles';
import { allTokens } from '@hopdrive/ui/tokens';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EmptyStateVariant =
  | 'default'   // secondaryAccent — empty table, zero data
  | 'secondary' // tertiaryAccent — calendar, schedules
  | 'search'    // neutral grey — no search results
  | 'error'     // error — something went wrong
  | 'warning'   // warning — first-time, welcome
  | 'info';    // info — informational

export interface EmptyStateProps {
  /** Resource name, e.g. "Regions", "Moves" — builds default title & description */
  resource: string;
  /** Override default title ("No {resource} Yet") */
  title?: string;
  /** Override default description */
  description?: string;
  /** Optional overline label above the headline (e.g. "Empty Table", "Error State") */
  overline?: string;
  /** Icon rendered inside the 96px badge. Defaults to InboxIcon. */
  icon?: React.ReactElement;
  /** Semantic variant — controls icon badge colors (default, secondary, search, error, warning, info) */
  variant?: EmptyStateVariant;
  /** Primary CTA label, e.g. "Add Region". Omit to hide. */
  ctaLabel?: string;
  /** Click handler for the primary CTA */
  onCtaClick?: () => void;
  /** Render the CTA as a link — pass the Link component */
  ctaComponent?: React.ElementType;
  /** "to" prop when ctaComponent is provided */
  ctaTo?: string;
  /** Optional secondary text button label */
  secondaryLabel?: string;
  /** Click handler for the secondary action */
  onSecondaryClick?: () => void;
  /** Render without Card wrapper — for embedding inside containers like DataCard */
  bare?: boolean;
}

// ---------------------------------------------------------------------------
// Variant color mapping (light / dark)
// ---------------------------------------------------------------------------

function getVariantColors(
  theme: Theme,
  variant: EmptyStateVariant
): { iconColor: string; iconBg: string } {
  const isDark = theme.palette.mode === 'dark';
  const p = theme.palette as any;

  // Use design system palette when available, fallback to MUI semantic colors
  const secondaryMain = p.secondaryAccent?.main ?? p.secondary?.main ?? '#4068a0';
  const tertiaryMain = p.tertiaryAccent?.main ?? p.info?.main ?? '#40a0b0';
  const errorMain = p.error?.main ?? '#f44336';
  const warningMain = p.warning?.main ?? '#ff9800';
  const infoMain = p.info?.main ?? '#2196f3';

  const variants: Record<EmptyStateVariant, { iconColor: string; iconBg: string }> = {
    default: {
      iconColor: isDark ? p.secondaryAccent?.[300] ?? secondaryMain : secondaryMain,
      iconBg: isDark ? alpha(secondaryMain, 0.12) : alpha(secondaryMain, 0.08),
    },
    secondary: {
      iconColor: isDark ? p.tertiaryAccent?.[300] ?? tertiaryMain : tertiaryMain,
      iconBg: isDark ? alpha(tertiaryMain, 0.12) : alpha(tertiaryMain, 0.08),
    },
    search: {
      iconColor: isDark ? p.grey?.[400] ?? '#9e9e9e' : p.grey?.[400] ?? '#9e9e9e',
      iconBg: isDark ? 'rgba(255,255,255,0.06)' : (p.grey?.[100] ?? '#f5f5f5'),
    },
    error: {
      iconColor: errorMain,
      iconBg: isDark ? alpha(errorMain, 0.10) : alpha(errorMain, 0.08),
    },
    warning: {
      iconColor: warningMain,
      iconBg: isDark ? alpha(warningMain, 0.12) : alpha(warningMain, 0.08),
    },
    info: {
      iconColor: infoMain,
      iconBg: isDark ? alpha(infoMain, 0.12) : alpha(infoMain, 0.08),
    },
  };

  return variants[variant];
}

// ---------------------------------------------------------------------------
// Elevation helper (matches project pattern)
// ---------------------------------------------------------------------------

const getElevation = (level: typeof allTokens.elevation.level1) =>
  level.layers
    .map(
      (l) =>
        `${l.offsetX}px ${l.offsetY}px ${l.blur}px ${l.spread}px rgba(0,0,0,${l.opacity})`
    )
    .join(', ');

// ---------------------------------------------------------------------------
// Typography scale (from design tokens)
// ---------------------------------------------------------------------------

const typography = {
  overline: { size: 11, weight: 600, lineHeight: 1.2 },
  h3: { size: 20, weight: 600, lineHeight: 1.3 },
  body2: { size: 14, lineHeight: 1.43 },
  button: { size: 14, weight: 600 },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Zero-data empty state card — matches the HopDrive design system.
 *
 * Reference: hopdrive-design-system/src/components/patterns/EmptyStatePage.tsx
 *
 * Layout: Card (level2) > icon badge (96px, semantic tint) > optional overline
 *         > h3 headline > body2 subtext (260px max) > primary CTA + secondary text
 */
export default function EmptyState({
  resource,
  title,
  description,
  overline,
  icon,
  variant = 'default',
  ctaLabel,
  onCtaClick,
  ctaComponent,
  ctaTo,
  secondaryLabel,
  onSecondaryClick,
  bare = false,
}: EmptyStateProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { iconColor, iconBg } = getVariantColors(theme, variant);

  const resolvedTitle = title ?? `No ${resource} Yet`;
  const resolvedDescription =
    description ??
    `You don't have any ${resource.toLowerCase()} yet. Get started by creating your first one.`;

  const cardBg = isDark
    ? (theme.palette as any).grounding?.[700] ?? theme.palette.background.paper
    : theme.palette.background.paper;

  const subtextColor = isDark ? 'grey.400' : 'grey.600';
  const overlineColor = isDark ? 'grey.600' : 'grey.500';
  const headlineColor = isDark ? 'text.primary' : 'text.primary';

  const contentSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    p: `${allTokens.spacing['3xl']}px ${allTokens.spacing.xl}px`,
    gap: `${allTokens.spacing.md}px`,
  };

  const content = (
    <>
      {/* Icon badge — 96px circle, semantic tint */}
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: iconBg,
          mb: `${allTokens.spacing.xs}px`,
        }}
      >
        {icon ? (
          React.cloneElement(icon, {
            sx: {
              fontSize: 48,
              color: iconColor,
              ...(icon.props as React.HTMLAttributes<HTMLElement> & { sx?: object })?.sx,
            },
          } as React.HTMLAttributes<HTMLElement>)
        ) : (
          <InboxIcon sx={{ fontSize: 48, color: iconColor }} />
        )}
      </Box>

      {/* Optional overline — 11px / 600 / uppercase */}
      {overline && (
        <Typography
          variant="overline"
          sx={{
            fontSize: typography.overline.size,
            fontWeight: typography.overline.weight,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: overlineColor,
          }}
        >
          {overline}
        </Typography>
      )}

      {/* Headline — h3: 20px / 600 / 1.3 */}
      <Typography
        sx={{
          fontFamily: '"Inter", "Inter Tight", sans-serif',
          fontSize: typography.h3.size,
          fontWeight: typography.h3.weight,
          lineHeight: typography.h3.lineHeight,
          letterSpacing: '-0.01em',
          color: headlineColor,
        }}
      >
        {resolvedTitle}
      </Typography>

      {/* Subtext — body2, maxWidth 260px */}
      <Typography
        sx={{
          fontSize: typography.body2.size,
          lineHeight: typography.body2.lineHeight,
          color: subtextColor,
          maxWidth: 260,
        }}
      >
        {resolvedDescription}
      </Typography>

      {/* Actions — primary CTA + secondary text button */}
      {(ctaLabel || secondaryLabel) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: `${allTokens.spacing.xs}px`,
            mt: `${allTokens.spacing.sm}px`,
          }}
        >
          {ctaLabel && (
            <Button
              variant="contained"
              disableElevation
              onClick={onCtaClick}
              {...(ctaComponent && ctaTo
                ? { component: ctaComponent, to: ctaTo }
                : {})}
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: typography.button.weight,
                fontSize: typography.button.size,
                textTransform: 'none',
                borderRadius: `${allTokens.radius.md}px`,
                px: `${allTokens.spacing.xl}px`,
                py: `${allTokens.spacing.sm}px`,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: getElevation(allTokens.elevation.level1),
                },
              }}
            >
              {ctaLabel}
            </Button>
          )}

          {secondaryLabel && (
            <Button
              variant="text"
              onClick={onSecondaryClick}
              sx={{
                fontWeight: 500,
                fontSize: typography.body2.size,
                textTransform: 'none',
                color: subtextColor,
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  color: isDark ? 'grey.300' : 'text.primary',
                },
              }}
            >
              {secondaryLabel}
            </Button>
          )}
        </Box>
      )}
    </>
  );

  if (bare) {
    return <Box sx={contentSx}>{content}</Box>;
  }

  return (
    <Card
      sx={{
        ...contentSx,
        bgcolor: cardBg,
        borderRadius: `${allTokens.radius.lg}px`,
        boxShadow: getElevation(allTokens.elevation.level2),
        transition: 'box-shadow 200ms ease-in-out',
        '&:hover': {
          boxShadow: getElevation(allTokens.elevation.level3),
        },
      }}
    >
      {content}
    </Card>
  );
}
